import React from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  Mail,
  Calendar,
  MapPin,
  Users,
  Smartphone,
  Quote,
  Film,
  ArrowRight,
  ArrowUpRight,
} from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const QUICK_FACTS = [
  { Icon: Calendar, label: 'Founded', value: '2026' },
  { Icon: MapPin, label: 'Headquarters', value: 'Israel' },
  { Icon: Users, label: 'Founder', value: 'Lev Kazaryan' },
  { Icon: Smartphone, label: 'Platforms', value: 'Android (iOS + Windows soon)' },
];

const ANGLES = [
  {
    title: 'Why motivational self-help fails',
    body: 'The science of unreliable motivation vs. durable structure — and why every Q1 wave of resolutions collapses by March.',
  },
  {
    title: 'The retention problem in personal-development apps',
    body: 'Why 90-day drop-off is the industry norm, and what changes when growth is structured instead of inspiring.',
  },
  {
    title: 'Skill trees for adult learning',
    body: 'What video games taught us about progression, mastery, and motivation that lasts past the dopamine spike.',
  },
  {
    title: 'The Community model',
    body: 'Replacing comparison-driven social feeds with accountability communities that hold a shared standard.',
  },
  {
    title: 'From streak anxiety to skill mastery',
    body: 'The goal was never the chain. It was the capability the chain was supposed to build. Levanta optimizes for the second.',
  },
];

const SCREENSHOTS = [
  {
    src: '/press/screenshots/screen-1-skills-tree.png',
    label: 'Challenges',
    sub: '21-day belief & mindset missions',
  },
  {
    src: '/press/screenshots/screen-2-tribe.png',
    label: 'Tribe',
    sub: 'Community circles + skill tribes',
  },
  {
    src: '/press/screenshots/screen-3-challenges.png',
    label: 'Skill Tree',
    sub: 'Foundation · Creation · Connection · Fuel',
  },
  {
    src: '/press/screenshots/screen-4-mission.png',
    label: 'Lesson view',
    sub: 'Video micro-lesson inside a skill module',
  },
  {
    src: '/press/screenshots/screen-5-lesson.png',
    label: 'Challenge',
    sub: 'Daily reflection + journal entry',
  },
];

const ASSETS = [
  {
    name: 'Levanta logo',
    sub: 'Transparent PNG, full brand mark',
    href: '/press/levanta-logo.png',
    testId: 'asset-logo-download',
  },
  {
    name: 'App icon',
    sub: 'High-res WEBP, app store / favicon',
    href: '/press/levanta-app-icon.webp',
    testId: 'asset-app-icon-download',
  },
  {
    name: 'Feature graphic',
    sub: 'PNG header — phone + feature icons',
    href: '/press/levanta-feature-graphic.png',
    testId: 'asset-feature-graphic-download',
  },
  {
    name: 'Levanta teaser',
    sub: 'MP4, 30-second product teaser video',
    href: '/press/levanta-teaser.mp4',
    testId: 'asset-teaser-download',
  },
];

const MediaPress = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-24 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="press-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            Media &amp; Press
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            For journalists{' '}
            <em className="italic font-light text-blue-600">in a hurry.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Quick facts, founder quotes, story angles and direct-download brand
            assets. Press contact at the bottom.
          </p>
          <div className="mt-9 inline-flex flex-wrap justify-center gap-3">
            <a
              href="#assets"
              data-testid="press-jump-assets"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/90 ring-1 ring-black/5 text-[#0b1f3a] text-sm font-semibold hover:bg-white transition-colors"
            >
              Download assets
            </a>
            <a
              href="mailto:press@levanta.life"
              data-testid="press-jump-email"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0b1f3a] text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
            >
              <Mail className="w-4 h-4" />
              press@levanta.life
            </a>
          </div>
        </div>
      </section>

      {/* THE 25-WORD VERSION — single bold paragraph. */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            The 25-word version
          </p>
          <p className="text-2xl md:text-4xl font-bold text-[#0b1f3a] leading-snug text-center">
            Levanta is a structured personal growth app built around{' '}
            <em className="italic font-light text-blue-600">
              discipline, skill trees and community
            </em>{' '}
            — an antidote to motivational self-help that doesn&rsquo;t stick.
          </p>
        </div>
      </section>

      {/* QUICK FACTS — left rail, fact grid right. */}
      <section className="px-6 py-16 md:py-20 bg-[#f4f8fc]">
        <div className="max-w-5xl mx-auto grid md:grid-cols-12 gap-x-10 gap-y-8">
          <div className="md:col-span-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
              At a glance
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a]">
              Levanta in{' '}
              <em className="italic font-light text-blue-600">four facts.</em>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 grid sm:grid-cols-2 gap-4">
            {QUICK_FACTS.map(({ Icon, label, value }) => (
              <div
                key={label}
                className="rounded-2xl bg-white ring-1 ring-black/5 p-5 flex items-start gap-4"
              >
                <span className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0b1f3a]/55">
                    {label}
                  </p>
                  <p className="font-bold text-[#0b1f3a] mt-0.5 leading-snug">
                    {value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE 120-WORD VERSION */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
            The 120-word version
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] mb-8">
            Long enough to{' '}
            <em className="italic font-light text-blue-600">copy-paste.</em>
          </h2>
          <div className="space-y-5 text-lg text-[#0b1f3a]/80 leading-relaxed">
            <p>
              Levanta is a personal-development system built on the idea that
              motivation is unreliable and structure wins. It organises growth
              across four domains — Foundation (inner life), Creation (work),
              Connection (relationships) and Fuel (joy) — with real skill paths
              that members progress through via a daily 10-minute cycle of
              Learning, Practice, Reflection and Tracking.
            </p>
            <p>
              Founded by Lev Kazaryan, an NLP Master Practitioner and author,
              Levanta combines structured long-term skill development with
              meaningful gamification — XP, levels and skill trees that
              reward real practice rather than shallow streak-chasing —
              supported by a community that holds members accountable.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER QUOTE — dark navy band. */}
      <section className="px-6 py-20 md:py-24 bg-[#0b1f3a] text-white">
        <div className="max-w-4xl mx-auto relative">
          <Quote
            className="absolute -top-8 left-0 md:-top-14 md:-left-10 w-16 h-16 md:w-28 md:h-28 text-blue-300/15 -scale-x-100"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <p className="relative text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-5">
            Founder, on the record
          </p>
          <blockquote className="relative text-2xl md:text-4xl font-semibold leading-snug text-white">
            &ldquo;We&rsquo;ve spent a decade consuming motivational content and
            wondering why nothing changed.{' '}
            <em className="italic font-light text-blue-300">It wasn&rsquo;t the information.</em>{' '}
            It was the lack of structure. Levanta is the system I wished
            existed.&rdquo;
          </blockquote>
          <p className="relative mt-7 text-white/65 text-sm uppercase tracking-[0.18em] font-semibold">
            — Lev Kazaryan, Founder &amp; CEO
          </p>
        </div>
      </section>

      {/* STORY ANGLES — numbered list. */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Story angles the founder can speak to
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-12">
            Five threads worth{' '}
            <em className="italic font-light text-blue-600">pulling.</em>
          </h2>
          <ol className="space-y-3">
            {ANGLES.map((a, i) => (
              <li
                key={a.title}
                className="rounded-2xl bg-[#fafbfd] ring-1 ring-black/5 p-5 md:p-6 flex gap-5 transition-all duration-300 hover:bg-white hover:ring-blue-200 hover:shadow-md"
              >
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0b1f3a] text-white text-sm font-mono font-bold flex items-center justify-center">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-[#0b1f3a] text-lg leading-snug mb-1">
                    {a.title}
                  </h3>
                  <p className="text-[#0b1f3a]/65 leading-relaxed">{a.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-10 text-center text-[#0b1f3a]/65">
            Want a quote, an interview or a custom angle?{' '}
            <a
              href="mailto:press@levanta.life?subject=Interview%20request"
              className="text-blue-600 font-semibold hover:text-blue-700 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600 transition-colors"
            >
              press@levanta.life
            </a>
          </p>
        </div>
      </section>

      {/* BRAND ASSETS */}
      <section
        id="assets"
        className="px-6 py-20 md:py-24 bg-gradient-to-b from-[#eaf3fb] to-white"
      >
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Brand assets
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-4">
            Use freely.{' '}
            <em className="italic font-light text-blue-600">No approval needed.</em>
          </h2>
          <p className="text-center text-[#0b1f3a]/65 max-w-2xl mx-auto mb-12">
            Direct-download files for any editorial coverage. Need a different
            format (vector, high-res, dark variant)? Email{' '}
            <a
              href="mailto:press@levanta.life?subject=Brand%20asset%20request"
              className="text-blue-600 font-semibold hover:text-blue-700 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600 transition-colors"
            >
              press@levanta.life
            </a>{' '}
            and we&rsquo;ll send it the same day.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ASSETS.map(({ name, sub, href, testId }) => {
              const isVideo = href.endsWith('.mp4');
              return (
                <a
                  key={testId}
                  href={href}
                  download
                  data-testid={testId}
                  className="group rounded-2xl bg-white ring-1 ring-black/5 p-4 md:p-6 flex items-center gap-3 md:gap-5 min-w-0 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:ring-blue-200"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {isVideo ? (
                      <Film className="w-5 h-5 md:w-6 md:h-6 text-blue-600" aria-hidden="true" />
                    ) : (
                      <img src={href} alt={`${name} thumbnail`} className="w-full h-full object-contain p-2" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#0b1f3a] truncate text-sm md:text-base">{name}</p>
                    {/* Sub-text wraps on mobile (2 lines max) so users actually
                        read the format hint instead of seeing it cut. Desktop
                        keeps single-line truncate since space is plentiful. */}
                    <p className="text-xs md:text-sm text-[#0b1f3a]/55 mt-0.5 leading-snug md:truncate">{sub}</p>
                  </div>
                  <span className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#0b1f3a]/5 text-[#0b1f3a] flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-200">
                    <Download className="w-4 h-4" />
                  </span>
                </a>
              );
            })}
          </div>

          {/* APP SCREENSHOTS — phone-mock gallery. Each thumbnail is a
              downloadable PNG so journalists can grab them without
              filing a separate request. */}
          <div className="mt-16 md:mt-20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3 text-center">
              App screenshots
            </p>
            <h3 className="text-2xl md:text-3xl font-black tracking-tight text-[#0b1f3a] text-center mb-10">
              Click any to{' '}
              <em className="italic font-light text-blue-600">download.</em>
            </h3>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5"
              data-testid="press-screenshots-grid"
            >
              {SCREENSHOTS.map(({ src, label, sub }) => (
                <a
                  key={src}
                  href={src}
                  download
                  data-testid={`press-screenshot-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                  className="group block rounded-2xl overflow-hidden bg-white ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-blue-200"
                >
                  <div className="relative aspect-[3/4] bg-[#eaf3fb] overflow-hidden">
                    <picture>
                      <source srcSet={src.replace('.png', '.webp')} type="image/webp" />
                      <img
                        src={src}
                        alt={`Levanta app screenshot — ${label} screen`}
                        loading="lazy"
                        width="800"
                        height="1067"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    </picture>
                    <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur text-[#0b1f3a] flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Download className="w-4 h-4" />
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="font-bold text-[#0b1f3a] text-sm">{label}</p>
                    <p className="text-xs text-[#0b1f3a]/55 mt-0.5 line-clamp-1">{sub}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRESS COVERAGE — empty state for now, but elegant. */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            Press coverage
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-10">
            Want to be the{' '}
            <em className="italic font-light text-blue-600">first?</em>
          </h2>
          <div
            data-testid="press-coverage-empty"
            className="rounded-3xl border-2 border-dashed border-[#0b1f3a]/15 bg-[#fafbfd] p-10 md:p-14 text-center"
          >
            <p className="text-lg md:text-xl text-[#0b1f3a]/70 leading-relaxed max-w-xl mx-auto">
              Selected mentions of Levanta will live here. The list grows as
              journalists cover the structured-growth category.
            </p>
            <a
              href="mailto:press@levanta.life?subject=Press%20coverage"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0b1f3a] text-white text-sm font-semibold hover:bg-blue-600 transition-colors duration-200"
            >
              Cover it first
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA — press contact + try the app. */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            One inbox.{' '}
            <em className="italic font-light text-blue-300">Two-day reply.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            For interviews, briefings, custom quotes or anything we forgot to put
            on this page.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="mailto:press@levanta.life"
              data-testid="press-cta-email"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              <Mail className="w-4 h-4" />
              press@levanta.life
            </a>
            <Link
              to="/download-app"
              data-testid="press-cta-download"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/5 text-white font-semibold ring-1 ring-white/20 hover:bg-white/10 transition-all duration-200"
            >
              Try the app
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default MediaPress;
